/* A layout controller only: retain APO controls, pricing, upload handlers and cart submission. */
(() => {
  'use strict';
  const boot = () => {
    const root = document.getElementById('sc-native-ordering-design');
    const product = document.getElementById('ProductSection');
    const config = window.SaunaNativeOrderingConfig?.[root?.dataset.productHandle];
    if (!root || !product || !config) return;
    const form = product.querySelector('form[action="/cart/add"]');
    if (!form) return;
    let groups = config.groups.map(ids => [...ids]);
    let colorConflict = false;
    const cloneColorHandles = new Set(['sauna-cushion','custom-barrel-sauna-cushion','sauna-cushion-golden-designs','sauna-cushion-health-mate-saunas','sauna-cushion-jacuzzi-saunas-clearlight-saunas','sauna-cushion-sunlighten']);
    // Resolve only the real native option wrapper; do not rewrite its ID,
    // control names, values, prices or order properties.
    const resolveGroups = () => {
      const nextGroups = config.groups.map(ids => [...ids]);
      if (!cloneColorHandles.has(root.dataset.productHandle)) return nextGroups;
      const candidates = [...product.querySelectorAll('.mw-option')].filter(o => {
        const title = o.querySelector('.mw-text--style-strong')?.textContent.replace(/\s+/g,' ').trim();
        return title === 'Choose Your Cushion Color' && /^o_\d+$/.test(o.id) && !!o.querySelector('input[type="radio"]');
      });
      if (candidates.length !== 1) return null;
      const chosen = candidates[0];
      const old = product.querySelector('#o_1989092');
      if (old && chosen !== old) return null;
      nextGroups[2] = nextGroups[2].map(id => id === '1989092' ? chosen.id.slice(2) : id);
      return nextGroups;
    };
    const steps = groups.map((ids,i)=>ids.length?i:null).filter(i=>i!==null);
    const pages = [...root.querySelectorAll('[data-sc-order-page]')];
    const destinations = pages.map(p => p.querySelector('[data-sc-order-fields]'));
    const progress = [...root.querySelectorAll('[data-sc-order-step]')];
    const next = root.querySelector('[data-sc-order-next]');
    const back = root.querySelector('[data-sc-order-back]');
    const status = root.querySelector('[data-sc-order-status]');
    let step = steps[0], mounted = false, queued = false;
    let photoDisclosure, hasChoices = false;
    const syncPhotos = () => {
      if (!photoDisclosure) return;
      const selected = groups.flat().some(id => {
        const option = document.getElementById('o_'+id);
        if (!option || option.closest('.mw-hidden,[hidden]')) return false;
        return [...option.querySelectorAll('select,input[type="radio"],input[type="checkbox"]')].some(field => {
          if (field.matches(':disabled') || field.closest('.mw-hidden,[hidden]')) return false;
          return field.tagName === 'SELECT' ? !!field.value.trim() : field.checked;
        });
      });
      if (selected && !hasChoices) photoDisclosure.open = false;
      else if (!selected && hasChoices) photoDisclosure.open = true;
      hasChoices = selected;
    };
    const shown = el => !!el && !el.closest('[hidden],.mw-hidden') && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
    const move = (el,dest) => { if (el && el.parentElement !== dest) dest.append(el); };
    const paintStep = (n,focus = false) => {
      step = n;
      form.dataset.scOrderStage = String(n);
      pages.forEach((p,i) => {
        p.removeAttribute('hidden');
        p.dataset.active = String(i === n);
        p.inert = i !== n;
        p.setAttribute('aria-hidden', String(i !== n));
      });
      progress.forEach((b,i) => {
        if (i === n) b.setAttribute('aria-current','step');
        else b.removeAttribute('aria-current');
      });
      back.hidden = n === 0;
      next.hidden = n === 3;
      next.textContent = steps[steps.indexOf(n)+1] === 3 ? 'Continue to review' : 'Continue';
      status.textContent = '';
      if (focus) {
        const heading = pages[n].querySelector('h3');
        heading.tabIndex = -1;
        heading.focus({preventScroll:true});
        requestAnimationFrame(() => requestAnimationFrame(() => root.scrollIntoView({behavior:'instant',block:'start'})));
      }
      product.dispatchEvent(new CustomEvent('sc:ordering-step',{detail:{step:n}}));
    };
    const optionsIn = n => [...pages[n].querySelectorAll('.mw-option')].filter(shown);
    const fail = (field,message,n) => {
      paintStep(n,true);
      status.textContent = message;
      if (field) {
        field.focus({preventScroll:true});
        field.scrollIntoView({block:'center',behavior:'instant'});
        if (field.validity && !field.validity.valid) field.reportValidity();
      }
      return false;
    };
    const validate = n => {
      for (const option of optionsIn(n)) {
        if (!option.classList.contains('mw-option--required')) continue;
        const inputs = [...option.querySelectorAll('input:not([type="hidden"]),select,textarea')].filter(el => !el.disabled && shown(el));
        const field = inputs.find(el => el.tagName === 'SELECT' || ['text','number','email','tel','radio','checkbox','file'].includes(el.type) || el.tagName === 'TEXTAREA');
        if (!field) continue;
        const label = option.querySelector('.mw-option__title,label')?.textContent.replace(/\s*\*\s*$/,'').trim() || 'this option';
        let missing = false;
        if (field.type === 'radio' || field.type === 'checkbox') missing = !inputs.some(el => el.checked);
        else if (field.type === 'file') missing = !option.querySelector('[data-filepond-item-state="processing-complete"]');
        else missing = !field.value.trim();
        if (missing) return fail(field,`Please complete ${label}.`,n);
      }
      for (const field of pages[n].querySelectorAll('input,select,textarea')) {
        if (field.disabled || !shown(field) || field.type === 'hidden' || field.type === 'file') continue;
        const dimension = field.matches('[data-sc-measure],[data-sc-corner-supplement],#sc-front-door-width') || config.measurements.includes(field.closest('.mw-option')?.id || '');
        if (dimension && field.value && !/^\d+\.(?:00|25|50|75)$/.test(field.value.trim())) return fail(field,'Enter whole inches, a decimal point, and .00, .25, .50 or .75 (for example, 48.25).',n);
        if (dimension && field.value && field.hasAttribute('max') && Number(field.value) > Number(field.getAttribute('max'))) return fail(field,`Enter a measurement up to ${field.getAttribute('max')} inches.`,n);
        if (!field.checkValidity()) return fail(field,field.validationMessage || 'Please check this field.',n);
      }
      if (n === 0) {
        const roof = document.getElementById('sc-online-roof');
        if (shown(roof) && roof.dataset.complete !== 'true') {
          const action = roof.querySelector('[data-sc-attach-roof]') || roof.querySelector('button');
          return fail(action,'Complete the roof measurements and attach the matching worksheet before continuing.',n);
        }
      }
      if (n === 1) {
        const door = document.getElementById('sc-door-width');
        if (shown(door) && door.dataset.complete !== 'true') return fail(door.querySelector('select'),'Choose your standard door opening and complete its width if requested.',n);
      }
      status.textContent = '';
      return true;
    };
    const mount = () => {
      queued = false;
      const container = product.querySelector('.mw-apo-container');
      const resolved = resolveGroups();
      colorConflict = !resolved;
      if (!container || colorConflict || !resolved.flat().every(id => product.querySelector('#o_'+id))) return;
      groups = resolved;
      // Keep the same APO container and real form controls so delegated app events remain intact.
      if (!mounted) container.prepend(root);
      groups.forEach((ids,i) => ids.forEach(id => {
        const option = document.getElementById('o_'+id);
        if (i === 2 && option.closest('.sc-color-choices')) move(option.closest('.sc-color-choices'),destinations[i]);
        else move(option,destinations[i]);
      }));
      const roof = document.getElementById('sc-online-roof');
      move(roof,destinations[0]);
      move(document.getElementById('sc-door-width'),destinations[1]);
      const units = roof?.querySelector('.sc-roof-types + .sc-custom-help');
      const unitsCopy = 'Enter inches with two decimal places ending in .00, .25, .50 or .75.';
      if (units && units.textContent !== unitsCopy) units.textContent = unitsCopy;
      for (const note of product.querySelectorAll('.sc-color-note,.sc-color-mobile-note')) {
        const copy = 'Illustrative preview. Colors and placement are approximate.';
        if (note.textContent !== copy) note.textContent = copy;
      }
      move(document.getElementById('sc-corner-measurements'),destinations[0]);
      if (!mounted) {
        const gallery = product.querySelector('.sc-product-gallery');
        const photos = gallery && [...gallery.children].filter(el => el.classList.contains('grid') || el.classList.contains('sc-highlights'));
        if (photos?.length && !gallery.querySelector('[data-sc-order-photos]')) {
          const disclosure = document.createElement('details');
          disclosure.dataset.scOrderPhotos = '';
          disclosure.open = true;
          const title = document.createElement('summary');
          title.textContent = 'View product photos & details';
          disclosure.append(title,...photos);
          gallery.append(disclosure);
        }
        photoDisclosure = gallery?.querySelector('[data-sc-order-photos]');
        mounted = true;
        root.hidden = false;
        form.classList.add('sc-native-step-form');
        product.querySelector('.sc-build-intro')?.setAttribute('hidden','');
        root.style.setProperty('--sc-order-count',String(steps.length));
        progress.forEach((button,i)=>{button.hidden=!steps.includes(i);if(steps.includes(i))button.querySelector('span').textContent=String(steps.indexOf(i)+1);});
        paintStep(steps[0]);
      }
      syncPhotos();
    };
    const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(mount); } };
    form.addEventListener('input',schedule);
    form.addEventListener('change',schedule);
    next.addEventListener('click',() => { if (validate(step)) paintStep(steps[Math.min(steps.length-1,steps.indexOf(step)+1)],true); });
    back.addEventListener('click',() => paintStep(steps[Math.max(0,steps.indexOf(step)-1)],true));
    progress.forEach((button,n) => button.addEventListener('click',() => {
      if (n > step) { for (const i of steps.filter(i=>i>=step&&i<n)) if (!validate(i)) return; }
      paintStep(n,true);
    }));
    product.addEventListener('click',event => {
      const link = event.target.closest('a[href^="#o_"]');
      if (!mounted || !link) return;
      const page = document.getElementById(link.getAttribute('href').slice(1))?.closest('[data-sc-order-page]');
      if (page) { event.preventDefault(); paintStep(Number(page.dataset.scOrderPage),true); }
    });
    form.addEventListener('invalid',event => {
      if (!mounted) return;
      const page = event.target.closest('[data-sc-order-page]');
      if (page && Number(page.dataset.scOrderPage) !== step) paintStep(Number(page.dataset.scOrderPage),true);
    },true);
    const guard = event => {
      if (!mounted || (event.type === 'click' && !event.target.closest('button[name="add"],input[type="submit"]'))) return;
      if (colorConflict || !resolveGroups()) { event.preventDefault(); event.stopImmediatePropagation(); status.textContent='The color choices are unavailable. Please refresh the page.'; return; }
      for (const i of steps) if (!validate(i)) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    };
    // Run ahead of app add-to-cart listeners without replacing their submission path.
    window.addEventListener('click',guard,true);
    form.addEventListener('submit',guard,true);
    new MutationObserver(schedule).observe(form,{childList:true,subtree:true});
    mount();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',boot,{once:true});
  else boot();
})();
